import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getComments, addComment, deleteComment } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { HiOutlineTrash } from 'react-icons/hi';
import { FiSend } from 'react-icons/fi';
import Modal from './Modal';

const POLL_INTERVAL = 10000;

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return 'A l\'instant';
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

export default function CommentThread({ show, onClose, projectId, testCase, onCommentCountChange }) {
  const { user, isAdmin } = useAuth();
  const [comments, setComments] = useState([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const loadComments = useCallback(async () => {
    if (!projectId || !testCase?.id) return;
    try {
      const { data } = await getComments(projectId, testCase.id);
      setComments(data);
      if (onCommentCountChange) onCommentCountChange(testCase.id, data.length);
    } catch { }
  }, [projectId, testCase?.id, onCommentCountChange]);

  useEffect(() => {
    if (show) {
      loadComments();
      const interval = setInterval(loadComments, POLL_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [show, loadComments]);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [comments]);

  const handleSend = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      await addComment(projectId, testCase.id, message.trim());
      setMessage('');
      await loadComments();
    } catch { }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDelete = async (commentId) => {
    try {
      await deleteComment(projectId, testCase.id, commentId);
      await loadComments();
    } catch { }
  };

  if (!testCase) return null;

  return (
    <Modal show={show} onClose={onClose} title={`Discussion - ${testCase.ct_id}`} size="md">
      <div className="comment-thread-subtitle">{testCase.cas_test}</div>

      <div className="comment-thread-list" ref={listRef}>
        {comments.length === 0 && (
          <div className="comment-thread-empty">Aucun message. Commencez la discussion !</div>
        )}
        {comments.map((c) => {
          const isOwn = c.user_id === user?.id;
          return (
            <div key={c.id} className={`comment-thread-msg ${isOwn ? 'own' : ''}`}>
              <div className="comment-thread-avatar" style={{ background: isOwn ? 'var(--green-500)' : 'var(--navy-400)' }}>
                {getInitials(c.full_name || c.username)}
              </div>
              <div className="comment-thread-bubble">
                <div className="comment-thread-meta">
                  <span className="comment-thread-author">{c.full_name || c.username}</span>
                  <span className="comment-thread-time">{timeAgo(c.created_at)}</span>
                  {(isOwn || isAdmin) && (
                    <button className="comment-thread-delete" onClick={() => handleDelete(c.id)} title="Supprimer">
                      <HiOutlineTrash />
                    </button>
                  )}
                </div>
                <div className="comment-thread-text">{c.message}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="comment-thread-input">
        <textarea
          className="form-control form-control-sm"
          rows={2}
          placeholder="Ecrire un message... (Entree pour envoyer)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          className="btn btn-primary btn-sm comment-thread-send"
          onClick={handleSend}
          disabled={!message.trim() || sending}
          title="Envoyer"
        >
          <FiSend />
        </button>
      </div>
    </Modal>
  );
}
